import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  Breadcrumbs,
  Link,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getApiErrorMessage } from "../../helpers/aws_api_helper";
import { getCustomerMetadata as getCustomerMetadataApi } from "../../helpers/auth_aws_helper";
import {
  createRules,
  clearBillingManagementMessages,
} from "../../store/billingManagement/billingManagementSlice";
import {
  BILLING_SCOPE_TYPE_INELIGIBLE_SERVICE,
  normalizeMetadataCodeList,
  ADJUSTMENT_PARTITION_ELIGIBLE,
  ADJUSTMENT_PARTITION_INELIGIBLE,
  mapLegacyAdjustmentTypeToTypeAndPartition,
  mapAdjustmentTypeAndPartitionToApiRuleType,
  migrateSelectedRulesAdjustmentPartitions,
} from "./awsUsageServiceScopePartitions";

const ReviewBillingRules = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  // Get rule blocks and metadata from location state
  const { ruleBlocks, customerOrgCode, metadata } = location.state || {};

  // Redux state
  const {
    isCreatingRules,
    createRulesSuccess,
    createRulesError,
  } = useSelector((state) => state.billingManagement);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Accordion UI state (persisted in sessionStorage so it holds when navigating back)
  const REVIEW_ACCORDION_KEY = customerOrgCode ? `billing_review_accordion_${customerOrgCode}` : null;
  const loadAccordionState = () => {
    if (!REVIEW_ACCORDION_KEY) return { expandedCategories: [], expandedRules: [] };
    try {
      const raw = sessionStorage.getItem(REVIEW_ACCORDION_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          expandedCategories: Array.isArray(parsed.expandedCategories) ? parsed.expandedCategories : [],
          expandedRules: Array.isArray(parsed.expandedRules) ? parsed.expandedRules : [],
        };
      }
    } catch (e) {
      console.error("Error loading review accordion state:", e);
    }
    return { expandedCategories: [], expandedRules: [] };
  };
  const [accordionState, setAccordionState] = useState(loadAccordionState);

  const [ineligibleServiceCodes, setIneligibleServiceCodes] = useState([]);
  const [isFetchingIneligibleServiceCodes, setIsFetchingIneligibleServiceCodes] = useState(false);
  const ineligibleServiceCodesSet = useMemo(
    () => new Set(ineligibleServiceCodes),
    [ineligibleServiceCodes]
  );

  useEffect(() => {
    if (!customerOrgCode) return;
    setIsFetchingIneligibleServiceCodes(true);
    getCustomerMetadataApi(customerOrgCode, { kind: "ineligible_service_codes", size: 200 })
      .then((response) => {
        const { status, ...responseData } = response;
        setIneligibleServiceCodes(normalizeMetadataCodeList(responseData));
      })
      .catch((error) => {
        console.error("Error fetching ineligible service codes:", error);
      })
      .finally(() => {
        setIsFetchingIneligibleServiceCodes(false);
      });
  }, [customerOrgCode]);

  // Restore accordion state when landing on review page (e.g. after navigating back from Create)
  useEffect(() => {
    if (REVIEW_ACCORDION_KEY && ruleBlocks) {
      setAccordionState(loadAccordionState());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [REVIEW_ACCORDION_KEY, ruleBlocks]);

  const saveAccordionState = (next) => {
    setAccordionState(next);
    if (REVIEW_ACCORDION_KEY) {
      try {
        sessionStorage.setItem(REVIEW_ACCORDION_KEY, JSON.stringify(next));
      } catch (e) {
        console.error("Error saving review accordion state:", e);
      }
    }
  };

  const toggleCategoryAccordion = (category) => {
    saveAccordionState({
      ...accordionState,
      expandedCategories: accordionState.expandedCategories.includes(category)
        ? accordionState.expandedCategories.filter((c) => c !== category)
        : [...accordionState.expandedCategories, category],
    });
  };

  const toggleRuleAccordion = (ruleKey) => {
    saveAccordionState({
      ...accordionState,
      expandedRules: accordionState.expandedRules.includes(ruleKey)
        ? accordionState.expandedRules.filter((k) => k !== ruleKey)
        : [...accordionState.expandedRules, ruleKey],
    });
  };

  // Redirect if no rule blocks
  useEffect(() => {
    if (!ruleBlocks || !customerOrgCode) {
      history.goBack();
    }
  }, [ruleBlocks, customerOrgCode, history]);

  // Storage key for persisting rule blocks
  const STORAGE_KEY = `billing_rule_blocks_${customerOrgCode}`;

  // Clear saved state
  const clearSavedState = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Error clearing saved state:", error);
    }
  };

  // Handle success
  useEffect(() => {
    if (createRulesSuccess) {
      setSnackbar({
        open: true,
        message: "Billing rules created successfully!",
        severity: "success",
      });
      // Clear saved state on successful creation
      clearSavedState();
      setTimeout(() => {
        history.push(`/billing-management/customer/${customerOrgCode}/rules`);
        dispatch(clearBillingManagementMessages());
      }, 1500);
    }
  }, [createRulesSuccess, history, customerOrgCode, dispatch]);

  // Handle error
  useEffect(() => {
    if (createRulesError) {
      const errorMessage = typeof createRulesError === "string" ? createRulesError : (getApiErrorMessage(createRulesError) || "Failed to create billing rules. Please try again.");
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    }
  }, [createRulesError]);

  // Clear Redux messages on unmount so they don't re-show when user navigates back
  useEffect(() => {
    return () => {
      dispatch(clearBillingManagementMessages());
    };
  }, [dispatch]);

  // Format date for display (stored as YYYY-MM or YYYY-MM-DD, avoid timezone shifts)
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "N/A";
    // Extract year and month directly so JS Date timezone doesn't shift us into previous month
    const match = String(dateString).match(/^(\d{4})-(\d{2})/);
    if (!match) return dateString;
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1; // 0-based
    const date = new Date(year, monthIndex, 1);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
  };

  // Build scopes from block + per-rule adjustment partition (eligible vs ineligible services).
  const buildScopes = (block, rule) => {
    const ruleCategory = rule.category;
    const scopes = [];
    const accountOp = block.scopeOperatorAccounts === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";
    const eligibleServiceOp = block.scopeOperatorServices === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";

    if (!block.allAccounts && block.selectedAccounts && block.selectedAccounts.length > 0) {
      block.selectedAccounts.forEach((account) => {
        scopes.push({
          scope_type: "LINKED_ACCOUNT",
          scope_value: account,
          operator: accountOp,
        });
      });
    }

    if (ruleCategory === "ADJUSTMENT") {
      const partition = rule.adjustmentPartition;
      const legacySvc = block.selectedServices || [];
      const hasSplit =
        Array.isArray(block.selectedServicesEligible) ||
        Array.isArray(block.selectedServicesIneligible);

      if (!partition && legacySvc.length > 0 && !hasSplit) {
        legacySvc.forEach((service) => {
          const isIneligible = ineligibleServiceCodesSet.has(service);
          scopes.push({
            scope_type: isIneligible ? BILLING_SCOPE_TYPE_INELIGIBLE_SERVICE : "SERVICE",
            scope_value: service,
            operator: isIneligible ? "INCLUDE" : eligibleServiceOp,
          });
        });
        const legacyReg = block.selectedRegions || [];
        const regionOp = block.scopeOperatorRegions === "EXCLUDE" ? "EXCLUDE" : "INCLUDE";
        legacyReg.forEach((region) => {
          scopes.push({
            scope_type: "REGION",
            scope_value: region,
            operator: regionOp,
          });
        });
        return scopes;
      }

      const isIneligiblePartition = partition === ADJUSTMENT_PARTITION_INELIGIBLE;
      const services = isIneligiblePartition
        ? block.selectedServicesIneligible || []
        : block.selectedServicesEligible || [];
      services.forEach((service) => {
        if (isIneligiblePartition) {
          scopes.push({
            scope_type: BILLING_SCOPE_TYPE_INELIGIBLE_SERVICE,
            scope_value: service,
            operator: "INCLUDE",
          });
        } else {
          scopes.push({
            scope_type: "SERVICE",
            scope_value: service,
            operator: eligibleServiceOp,
          });
        }
      });
      const regions = isIneligiblePartition
        ? block.selectedRegionsIneligible || []
        : block.selectedRegionsEligible || [];
      const regionOp = isIneligiblePartition
        ? (block.scopeOperatorRegionsIneligible === "EXCLUDE" ? "EXCLUDE" : "INCLUDE")
        : (block.scopeOperatorRegions === "EXCLUDE" ? "EXCLUDE" : "INCLUDE");
      regions.forEach((region) => {
        scopes.push({
          scope_type: "REGION",
          scope_value: region,
          operator: regionOp,
        });
      });
    }
    return scopes;
  };

  // Format date for API - expects YYYY-MM format, returns YYYY-MM-01
  const formatDateForAPI = (dateString) => {
    if (!dateString) return "";
    // If dateString is already in YYYY-MM format, append -01
    if (dateString.match(/^\d{4}-\d{2}$/)) {
      return `${dateString}-01`;
    }
    // Otherwise, parse and format
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    return `${year}-${month}-01`;
  };

  // Get category label and description
  const getCategoryInfo = (category) => {
    if (category === "ADJUSTMENT") {
      return { label: "AWS Usage - Adjustments", description: "Apply percentage based markup or discounts to the AWS Usage charges" };
    }
    if (metadata?.categories) {
      const categoryData = metadata.categories.find((cat) => cat.category === category);
      if (categoryData) {
        return {
          label: categoryData.label,
          description: categoryData.description,
        };
      }
    }
    // Fallback mapping
    const categoryMap = {
      TRANSFER: { label: "Transfers", description: "Transfer a percentage of cost between entities." },
      FEE: { label: "Fees", description: "Apply a fee flag to matching usage." },
      EXCLUSION: { label: "Exclusions", description: "Exclude matching usage from billing." },
      CUSTOM: { label: "Custom charges", description: "Add a fixed custom charge." },
      ADJUSTMENT: { label: "AWS Usage - Adjustments", description: "Apply percentage based markup or discounts to the AWS Usage charges" },
    };
    return categoryMap[category] || { label: category, description: "" };
  };

  // Get type label and description
  const getTypeInfo = (type, category) => {
    if (metadata?.categories && category) {
      const categoryData = metadata.categories.find((cat) => cat.category === category);
      if (categoryData?.types) {
        const typeData = categoryData.types.find((t) => t.type === type);
        if (typeData) {
          return {
            label: typeData.label,
            description: typeData.description,
          };
        }
      }
    }
    // Fallback mapping
    const typeMap = {
      SPP: { label: "SPP", description: "Savings plans and reserved instance coverage." },
      BUNDLE_DISCOUNTS: { label: "Bundle discounts", description: "Bundle discount line items." },
      TAX: { label: "Tax", description: "Tax-related charges." },
      AWS_CREDITS: { label: "AWS credits", description: "AWS credits and credits-related items." },
      RESERVE_INSTANCE: { label: "Reserved instance", description: "Reserved instance charges." },
      SAVINGS_PLAN: { label: "Savings plan", description: "Savings plan charges." },
      AWS_SUPPORT: { label: "AWS support", description: "AWS support charges." },
      AWS_MARKETPLACE: { label: "AWS Marketplace", description: "AWS Marketplace charges." },
      FIXED_COST: { label: "Fixed cost", description: "Add a fixed amount to the bill." },
      MARGIN: { label: "Margin", description: "Increase costs by a percentage." },
      DISCOUNT: { label: "Discount", description: "Decrease costs by a percentage." },
    };
    return typeMap[type] || { label: type, description: "" };
  };

  // Cosmetic UI only: TRANSFER types listed after AWS_MARKETPLACE in metadata appear under "Ineligible Services"
  const partitionTransferRulesForDisplay = (cat, ruleList) => {
    if (cat !== "TRANSFER") {
      return { eligible: ruleList, ineligible: [] };
    }
    const transferCat = metadata?.categories?.find((c) => c.category === "TRANSFER");
    const typeList = transferCat?.types || [];
    const idx = typeList.findIndex((t) => t.type === "AWS_MARKETPLACE");
    if (idx < 0) {
      return { eligible: ruleList, ineligible: [] };
    }
    const ineligibleSet = new Set(typeList.slice(idx + 1).map((t) => t.type));
    return {
      eligible: ruleList.filter((r) => !ineligibleSet.has(r.displayType || r.rule_type)),
      ineligible: ruleList.filter((r) => ineligibleSet.has(r.displayType || r.rule_type)),
    };
  };

  // Handle create rules
  const handleCreateRules = () => {
    if (!ruleBlocks || ruleBlocks.length === 0) {
      setSnackbar({
        open: true,
        message: "No rules to create.",
        severity: "error",
      });
      return;
    }

    // Build rules array for API (each block can have multiple selected rule types)
    const rulesArray = [];

    const customTypeName = metadata?.categories?.find((c) => c.category === "CUSTOM")?.types?.[0]?.type ?? null;

    ruleBlocks.forEach((block) => {
      const selectedRules = block.selectedRules || [];
      // Legacy: no selectedRules but has rule_category + rule_type or selectedTypes
      let rulesToEmit =
        selectedRules.length > 0
          ? selectedRules
          : block.selectedTypes?.length > 0
            ? block.selectedTypes.map((type) => ({
                category: block.rule_category,
                type,
                parameters: { enabled: true },
              }))
            : block.rule_category && block.rule_type
              ? [{ category: block.rule_category, type: block.rule_type, parameters: block.parameters || {} }]
              : [];

      // Exclude ADJUSTMENT/FIXED_COST (or same type as CUSTOM) – fixed cost is only emitted as CUSTOM
      if (customTypeName) {
        rulesToEmit = rulesToEmit.filter(
          (r) => !(r.category === "ADJUSTMENT" && r.type === customTypeName)
        );
      }
      // Exclude CUSTOM rows that are toggled off (enabled === false)
      rulesToEmit = rulesToEmit.filter(
        (r) => r.category !== "CUSTOM" || r.parameters?.enabled !== false
      );

      rulesToEmit = migrateSelectedRulesAdjustmentPartitions(rulesToEmit);

      rulesToEmit.forEach((rule) => {
        const parameters = { ...(rule.parameters || {}) };
        delete parameters._fullPartial;
        const baseAdjustmentType =
          rule.category === "ADJUSTMENT"
            ? mapLegacyAdjustmentTypeToTypeAndPartition(rule.type).type
            : rule.type;
        const ruleType =
          rule.category === "ADJUSTMENT"
            ? mapAdjustmentTypeAndPartitionToApiRuleType(rule.type, rule.adjustmentPartition)
            : rule.type;
        const typeLabel = getTypeInfo(baseAdjustmentType, rule.category).label;
        const partitionLabel =
          rule.category === "ADJUSTMENT" && rule.adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE
            ? " (Ineligible)"
            : rule.category === "ADJUSTMENT" && rule.adjustmentPartition === ADJUSTMENT_PARTITION_ELIGIBLE
              ? " (Eligible)"
              : "";
        rulesArray.push({
          name:
            rulesToEmit.length > 1 ? `${block.name} - ${typeLabel}${partitionLabel}` : block.name,
          description: block.description || undefined,
          rule_category: rule.category,
          rule_type: ruleType,
          cost_measurement: block.cost_measurement,
          status: block.status,
          frequency: block.frequency,
          start_date: block.frequency === "RECURRING" ? formatDateForAPI(block.start_date) : undefined,
          end_date: block.frequency === "RECURRING"
            ? (block.no_end_date ? null : formatDateForAPI(block.end_date))
            : undefined,
          scopes: buildScopes(block, rule),
          configuration: {
            parameters,
          },
        });
      });
    });

    dispatch(createRules({ customerOrgCode, rules: rulesArray }));
  };

  if (!ruleBlocks || !customerOrgCode) {
    return null;
  }

  const reviewNeedsIneligibleMetadata = ruleBlocks.some((block) => {
    const selectedRules = block.selectedRules || [];
    const hasAdjustment = selectedRules.some((r) => r.category === "ADJUSTMENT");
    const nInelig = (block.selectedServicesIneligible || []).length;
    const nLegacy = (block.selectedServices || []).length;
    return hasAdjustment && (nInelig > 0 || nLegacy > 0);
  });

  // Count API rules to be created (each selected type in a block = one rule), not rule block rows
  const customTypeNameForCount =
    metadata?.categories?.find((c) => c.category === "CUSTOM")?.types?.[0]?.type ?? null;
  const rulesCount = ruleBlocks.reduce((total, block) => {
    const selectedRules = block.selectedRules || [];
    let rulesToEmit =
      selectedRules.length > 0
        ? selectedRules
        : block.selectedTypes?.length > 0
          ? block.selectedTypes.map((type) => ({
              category: block.rule_category,
              type,
              parameters: { enabled: true },
            }))
          : block.rule_category && block.rule_type
            ? [{ category: block.rule_category, type: block.rule_type, parameters: block.parameters || {} }]
            : [];
    if (customTypeNameForCount) {
      rulesToEmit = rulesToEmit.filter(
        (r) => !(r.category === "ADJUSTMENT" && r.type === customTypeNameForCount)
      );
    }
    rulesToEmit = rulesToEmit.filter(
      (r) => r.category !== "CUSTOM" || r.parameters?.enabled !== false
    );
    return total + rulesToEmit.length;
  }, 0);

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh", pb: 8, mb: "3rem" }}>
      {/* Breadcrumbs with Back Button */}
      <Box sx={{ mb: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Breadcrumbs
          separator={
            <Box
              component="span"
              sx={{
                color: "#cbd5e1",
                fontSize: "0.875rem",
                mx: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              /
            </Box>
          }
          aria-label="breadcrumb"
          sx={{
            "& .MuiBreadcrumbs-ol": {
              flexWrap: "nowrap",
            },
          }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push("/billing-management")}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Management
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Billing Rules
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => history.push(`/billing-management/customer/${customerOrgCode}/rules/create`)}
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 400,
              transition: "color 0.2s ease",
              "&:hover": {
                color: "#1e293b",
              },
              cursor: "pointer",
              px: 0.5,
            }}
          >
            Create Billing {rulesCount > 1 ? "Rules" : "Rule"}
          </Link>
          <Typography
            variant="body2"
            sx={{
              color: "#3b82f6",
              fontSize: "0.875rem",
              fontWeight: 600,
              px: 0.5,
            }}
          >
            Review
          </Typography>
        </Breadcrumbs>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => {
            // Don't clear state when going back - let user continue editing
            history.push(`/billing-management/customer/${customerOrgCode}/rules/create`);
          }}
          sx={{
            textTransform: "none",
            color: "#64748b",
            fontSize: "0.875rem",
            fontWeight: 500,
            "&:hover": {
              backgroundColor: alpha("#3b82f6", 0.1),
              color: "#3b82f6",
            },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Rules Summary Header */}
      <Card sx={{ mb: 3, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#ffffff" }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", mb: 1.5 }}>
            Review Your Billing Rules
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.875rem", mb: 2, lineHeight: 1.6 }}>
            Please carefully review all the billing rules below before creating them. These rules will be applied to the customer's billing and will affect how costs are calculated, transferred, or adjusted. You can go back to make changes if needed.
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#3b82f6",
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500 }}>
                {rulesCount} {rulesCount === 1 ? "Rule" : "Rules"} Configured
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#10b981",
                }}
              />
              <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 500 }}>
                Rules grouped by category
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Group Rules by Category */}
      {(() => {
        // Process all rules from all blocks (from selectedRules or legacy shape)
        const customTypeName = metadata?.categories?.find((c) => c.category === "CUSTOM")?.types?.[0]?.type ?? null;
        const allRules = [];
        ruleBlocks.forEach((block, blockIndex) => {
          const selectedRules = block.selectedRules || [];
          let rulesToShow =
            selectedRules.length > 0
              ? selectedRules
              : block.selectedTypes?.length > 0
                ? block.selectedTypes.map((type) => ({ category: block.rule_category, type, parameters: {} }))
                : block.rule_category && block.rule_type
                  ? [{ category: block.rule_category, type: block.rule_type, parameters: block.parameters || {} }]
                  : [];
          // Don't show ADJUSTMENT/FIXED_COST here – fixed cost is only under Custom
          if (customTypeName) {
            rulesToShow = rulesToShow.filter(
              (r) => !(r.category === "ADJUSTMENT" && r.type === customTypeName)
            );
          }
          // Don't show CUSTOM rows that are toggled off
          rulesToShow = rulesToShow.filter(
            (r) => r.category !== "CUSTOM" || r.parameters?.enabled !== false
          );
          rulesToShow = migrateSelectedRulesAdjustmentPartitions(rulesToShow);
          rulesToShow.forEach((rule) => {
            const params = rule.parameters || {};
            let displayType = rule.type;
            if (rule.category === "ADJUSTMENT") {
              displayType = mapLegacyAdjustmentTypeToTypeAndPartition(rule.type).type;
            }
            const typeLabelForName = getTypeInfo(displayType, rule.category).label;
            const partitionLabel =
              rule.category === "ADJUSTMENT" && rule.adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE
                ? " (Ineligible)"
                : rule.category === "ADJUSTMENT" && rule.adjustmentPartition === ADJUSTMENT_PARTITION_ELIGIBLE
                  ? " (Eligible)"
                  : "";
            const typeSuffix = rulesToShow.length > 1 ? ` — ${typeLabelForName}` : "";
            allRules.push({
              ...block,
              rule_category: rule.category,
              displayType,
              rule_type: rule.type,
              adjustmentPartition: rule.adjustmentPartition,
              parameters: params,
              displayName: `${block.name}${typeSuffix}${partitionLabel}`,
              blockIndex,
            });
          });
        });

        // Group by category
        const groupedByCategory = allRules.reduce((acc, rule) => {
          const category = rule.rule_category;
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(rule);
          return acc;
        }, {});
        Object.keys(groupedByCategory).forEach((cat) => {
          if (cat !== "ADJUSTMENT") return;
          groupedByCategory[cat].sort((a, b) => {
            const rank = (r) =>
              r.adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE ? 1 : 0;
            return rank(a) - rank(b);
          });
        });

        return Object.entries(groupedByCategory)
          .sort(([a], [b]) => {
            if (a === "TRANSFER" && b !== "TRANSFER") return -1;
            if (b === "TRANSFER" && a !== "TRANSFER") return 1;
            return 0;
          })
          .map(([category, rules]) => (
          <Accordion
            key={category}
            expanded={accordionState.expandedCategories.includes(category)}
            onChange={() => toggleCategoryAccordion(category)}
            disableGutters
            square={false}
            sx={{
              mb: 2,
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 20, color: "#64748b" }} />}
              sx={{
                backgroundColor: "#f8fafc",
                px: 2.5,
                py: 2,
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                },
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "1rem", mb: 0.5 }}>
                  {getCategoryInfo(category).label}
                </Typography>
                {getCategoryInfo(category).description && (
                  <Typography sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                    {getCategoryInfo(category).description}
                  </Typography>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ p: 2.5 }}>
                {(() => {
                  const renderRuleAccordion = (rule, ruleIndex, list) => {
                  const ruleKey = `${category}-${rule.blockIndex}-${rule.displayType || rule.rule_type || ""}-${rule.adjustmentPartition || "x"}-${ruleIndex}`;
                  const isAdj = rule.rule_category === "ADJUSTMENT";
                  const isEligAdj = isAdj && rule.adjustmentPartition === ADJUSTMENT_PARTITION_ELIGIBLE;
                  const isInelAdj = isAdj && rule.adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE;
                  const accent =
                    isEligAdj ? "#2563eb" : isInelAdj ? "#d97706" : "#3b82f6";
                  return (
                  <Accordion
                    key={ruleKey}
                    expanded={accordionState.expandedRules.includes(ruleKey)}
                    onChange={() => toggleRuleAccordion(ruleKey)}
                    disableGutters
                    square={false}
                    sx={{
                      mb: ruleIndex < list.length - 1 ? 2 : 0,
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      ...(isAdj ? { borderLeft: `4px solid ${accent}` } : {}),
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "#64748b" }} />}
                      sx={{
                        backgroundColor: isInelAdj ? alpha("#d97706", 0.04) : "#f9fafb",
                        px: 2,
                        py: 1.5,
                        "& .MuiAccordionSummary-content": {
                          margin: 0,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          width: "100%",
                          flexWrap: "wrap",
                          rowGap: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: "8px",
                            backgroundColor: alpha(accent, isAdj ? 0.14 : 0.08),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: accent }}>
                            {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).label.charAt(0)}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: "1 1 160px", minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: "#64748b",
                              textTransform: "uppercase",
                              letterSpacing: "0.02em",
                              mb: 0.35,
                            }}
                          >
                            {getCategoryInfo(rule.rule_category).label}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mb: 0.25 }}>
                            <Typography
                              variant="subtitle1"
                              component="span"
                              sx={{ fontWeight: 600, color: "#1e293b", fontSize: "0.95rem" }}
                            >
                              {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).label}
                            </Typography>
                            {isEligAdj && (
                              <Chip
                                label="Eligible services"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.6875rem",
                                  fontWeight: 700,
                                  backgroundColor: alpha("#2563eb", 0.12),
                                  color: "#1d4ed8",
                                  border: "1px solid",
                                  borderColor: alpha("#2563eb", 0.35),
                                }}
                              />
                            )}
                            {isInelAdj && (
                              <Chip
                                label="Ineligible services"
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: "0.6875rem",
                                  fontWeight: 700,
                                  backgroundColor: alpha("#d97706", 0.14),
                                  color: "#b45309",
                                  border: "1px solid",
                                  borderColor: alpha("#d97706", 0.4),
                                }}
                              />
                            )}
                          </Box>
                          <Typography
                            sx={{
                              color: "#64748b",
                              fontSize: "0.8125rem",
                              lineHeight: 1.35,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {rule.displayName || rule.name}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: { xs: 0, sm: "auto" } }}>
                        <Chip
                          label={rule.frequency === "RECURRING" ? "Recurring" : "One-time"}
                          size="small"
                          sx={{
                            backgroundColor: rule.frequency === "RECURRING" ? alpha("#3b82f6", 0.1) : alpha("#10b981", 0.1),
                            color: rule.frequency === "RECURRING" ? "#3b82f6" : "#10b981",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                          }}
                        />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, py: 2 }}>
                      {/* Type description and rule description */}
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.75,
                          backgroundColor: "#f8fafc",
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).description && (
                          <Typography sx={{ color: "#64748b", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                            {getTypeInfo(rule.displayType || rule.rule_type || "", rule.rule_category).description}
                          </Typography>
                        )}
                        {rule.description && (
                          <Box
                            sx={{
                              mt: 1.5,
                              pt: 1.25,
                              borderTop: "1px solid #e2e8f0",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500, mb: 0.5 }}>
                              Rule Description
                            </Typography>
                            <Typography sx={{ color: "#1e293b", fontSize: "0.875rem", fontStyle: "italic", lineHeight: 1.5 }}>
                              "{rule.description}"
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Rule Name */}
                      {rule.name && (
                        <Box
                          sx={{
                            p: 2,
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                            mb: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1, textTransform: "uppercase" }}>
                            Rule Name
                          </Typography>
                          <Typography sx={{ fontSize: "0.9375rem", color: "#1e293b", fontWeight: 600 }}>
                            {rule.name}
                          </Typography>
                        </Box>
                      )}

                      {/* Rule Details in Boxes */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {/* Parameters */}
                        {(() => {
                        const params = rule.parameters || {};
                        // For boolean-only categories, the parameter is always enabled=true
                        const rawDisplayParams = rule.selectedTypes && rule.selectedTypes.length > 0
                          ? { enabled: true }
                          : params;
                        const displayParams = Object.fromEntries(
                          Object.entries(rawDisplayParams).filter(([key]) => !String(key).startsWith("_"))
                        );

                        if (Object.keys(displayParams).length > 0) {
                          const getParamMeta = (key) => {
                            if (metadata?.categories) {
                              const categoryData = metadata.categories.find(
                                (cat) => cat.category === rule.rule_category
                              );
                              if (categoryData?.types) {
                                const typeData = categoryData.types.find(
                                  (t) => t.type === (rule.displayType || rule.rule_type)
                                );
                                if (typeData?.required_parameters) {
                                  return typeData.required_parameters.find((p) => p.key === key) || null;
                                }
                              }
                            }
                            return null;
                          };

                          return (
                            <Box
                              sx={{
                                p: 2.5,
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                                Configuration Parameters
                              </Typography>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2, lineHeight: 1.5 }}>
                                These parameters define how this rule will be applied to the billing calculations.
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {Object.entries(displayParams).map(([key, value]) => {
                                  const paramMeta = getParamMeta(key);
                                  const paramDesc = paramMeta?.description || "";
                                  const label =
                                    paramMeta?.label ||
                                    key
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (c) => c.toUpperCase());
                                  return (
                                    <Box
                                      key={key}
                                      sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        alignItems: { xs: "stretch", sm: "flex-start" },
                                        justifyContent: "space-between",
                                        gap: { xs: 1, sm: 2 },
                                        p: 1.5,
                                        backgroundColor: "#f8fafc",
                                        borderRadius: "8px",
                                        border: "1px solid #e2e8f0",
                                      }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: paramDesc ? 0.5 : 0 }}>
                                            {label}
                                          </Typography>
                                          {paramDesc ? (
                                            <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.45 }}>
                                              {paramDesc}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                        <Typography
                                          sx={{
                                            fontSize: "0.9375rem",
                                            color: "#1d4ed8",
                                            fontWeight: 700,
                                            flexShrink: 0,
                                            alignSelf: { xs: "flex-start", sm: "center" },
                                            px: 1.25,
                                            py: 0.5,
                                            borderRadius: "6px",
                                            backgroundColor: alpha("#3b82f6", 0.08),
                                          }}
                                        >
                                          {typeof value === "boolean" ? (value ? "Enabled" : "Disabled") : String(value)}
                                          {key === "percent" && "%"}
                                          {key === "amount" && "$"}
                                        </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          );
                        }
                        return null;
                      })()}

                        {/* Frequency */}
                        <Box
                          sx={{
                            p: 2.5,
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            backgroundColor: "#ffffff",
                          }}
                        >
                          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                            Frequency
                          </Typography>
                          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2, lineHeight: 1.5 }}>
                            How often this rule is applied to billing.
                          </Typography>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            <Box
                              sx={{
                                p: 1.5,
                                backgroundColor: "#f8fafc",
                                borderRadius: "6px",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box>
                                  <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 0.5 }}>
                                    Frequency Type
                                  </Typography>
                                  <Typography sx={{ fontSize: "0.8125rem", color: "#64748b" }}>
                                    {rule.frequency === "RECURRING"
                                      ? "This rule will be applied continuously on each billing cycle."
                                      : "This rule will be applied immediately and remain in effect."}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={rule.frequency === "RECURRING" ? "Recurring" : "One-time"}
                                  size="small"
                                  sx={{
                                    backgroundColor: rule.frequency === "RECURRING" ? alpha("#3b82f6", 0.1) : alpha("#10b981", 0.1),
                                    color: rule.frequency === "RECURRING" ? "#3b82f6" : "#10b981",
                                    fontWeight: 600,
                                    fontSize: "0.8125rem",
                                  }}
                                />
                              </Box>
                            </Box>
                            {rule.frequency === "RECURRING" && (
                              <Box
                                sx={{
                                  p: 1.5,
                                  backgroundColor: "#f8fafc",
                                  borderRadius: "6px",
                                  border: "1px solid #e2e8f0",
                                }}
                              >
                                <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 0.5 }}>
                                  Active Period
                                </Typography>
                                <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                  The rule will be active during this time period.
                                </Typography>
                                <Typography sx={{ fontSize: "0.9375rem", color: "#1e293b", fontWeight: 500 }}>
                                  {formatDateForDisplay(rule.start_date)} → {formatDateForDisplay(rule.end_date)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>

                        {/* Scope Filters (Services, Regions, and Accounts) */}
                        {(() => {
                        let svcList = [];
                        let regList = [];
                        let regionOpKey = rule.scopeOperatorRegions || "INCLUDE";
                        let serviceOpKey = rule.scopeOperatorServices || "INCLUDE";
                        if (rule.rule_category === "ADJUSTMENT") {
                          if (rule.adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE) {
                            svcList = rule.selectedServicesIneligible || [];
                            regList = rule.selectedRegionsIneligible || [];
                            regionOpKey = rule.scopeOperatorRegionsIneligible || "INCLUDE";
                          } else if (rule.adjustmentPartition === ADJUSTMENT_PARTITION_ELIGIBLE) {
                            svcList = rule.selectedServicesEligible || [];
                            regList = rule.selectedRegionsEligible || [];
                          } else {
                            svcList = rule.selectedServices || [];
                            regList = rule.selectedRegions || [];
                          }
                        } else {
                          svcList = rule.selectedServices || [];
                          regList = rule.selectedRegions || [];
                        }
                        const hasServices = svcList.length > 0;
                        const hasRegions = regList.length > 0;
                        const hasAccountFilter = !rule.allAccounts && rule.selectedAccounts && rule.selectedAccounts.length > 0;
                        const isAllAccounts = rule.allAccounts !== false;
                        const hasAnyFilter = hasAccountFilter || hasServices || hasRegions;

                        if (hasAnyFilter) {
                          return (
                            <Box
                              sx={{
                                p: 2.5,
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                backgroundColor: "#ffffff",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1.5, textTransform: "uppercase" }}>
                                Scope Filters
                              </Typography>
                              <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 2, lineHeight: 1.5 }}>
                                This rule will only apply to the selected accounts, services, and/or regions.
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {/* Linked Accounts */}
                                {isAllAccounts && (hasServices || hasRegions) && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      Linked Accounts
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      This rule applies to all accounts associated with this customer.
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      <Chip
                                        label="All Linked Accounts"
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha("#10b981", 0.1),
                                          color: "#10b981",
                                          fontWeight: 600,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                )}
                                {hasAccountFilter && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      Linked Accounts ({rule.selectedAccounts.length}) — {(rule.scopeOperatorAccounts || "INCLUDE") === "EXCLUDE" ? "Exclude" : "Include"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      {(rule.scopeOperatorAccounts || "INCLUDE") === "EXCLUDE"
                                        ? "This rule will exclude charges from these AWS accounts."
                                        : "This rule applies only to charges from these AWS accounts."}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      {rule.selectedAccounts.map((account) => (
                                        <Chip
                                          key={account}
                                          label={account}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #e2e8f0",
                                            fontWeight: 500,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {hasServices && (() => {
                                  const chipSx = {
                                    backgroundColor: "#ffffff",
                                    color: "#475569",
                                    border: "1px solid #e2e8f0",
                                    fontWeight: 500,
                                  };
                                  const isIneligibleScope =
                                    rule.rule_category === "ADJUSTMENT" &&
                                    rule.adjustmentPartition === ADJUSTMENT_PARTITION_INELIGIBLE;
                                  const svcTitle = isIneligibleScope
                                    ? `Ineligible services (${svcList.length})`
                                    : `Eligible services (${svcList.length}) — ${serviceOpKey === "EXCLUDE" ? "Exclude" : "Include"}`;
                                  const svcBody = isIneligibleScope
                                    ? "The rule is limited to the service codes you selected from your organization’s ineligible list."
                                    : serviceOpKey === "EXCLUDE"
                                      ? "This rule will exclude charges from these AWS services."
                                      : "This rule applies only to charges from these AWS services.";
                                  return (
                                    <Box
                                      sx={{
                                        p: 1.5,
                                        backgroundColor: "#f8fafc",
                                        borderRadius: "6px",
                                        border: "1px solid #e2e8f0",
                                      }}
                                    >
                                      <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600 }}>
                                        AWS Services ({svcList.length})
                                      </Typography>
                                      <Typography sx={{ fontSize: "0.8125rem", color: "#475569", fontWeight: 600, mb: 0.5, mt: 1 }}>
                                        {svcTitle}
                                      </Typography>
                                      <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 0.75 }}>
                                        {svcBody}
                                      </Typography>
                                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {svcList.map((service) => (
                                          <Chip key={service} label={service} size="small" sx={chipSx} />
                                        ))}
                                      </Box>
                                    </Box>
                                  );
                                })()}
                                {hasRegions && (
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      backgroundColor: "#f8fafc",
                                      borderRadius: "6px",
                                      border: "1px solid #e2e8f0",
                                    }}
                                  >
                                    <Typography sx={{ fontSize: "0.875rem", color: "#1e293b", fontWeight: 600, mb: 1 }}>
                                      AWS Regions ({regList.length}) — {regionOpKey === "EXCLUDE" ? "Exclude" : "Include"}
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", mb: 1 }}>
                                      {regionOpKey === "EXCLUDE"
                                        ? "This rule will exclude charges from these AWS regions."
                                        : "This rule applies only to charges from these AWS regions."}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                      {regList.map((region) => (
                                        <Chip
                                          key={region}
                                          label={region}
                                          size="small"
                                          sx={{
                                            backgroundColor: "#ffffff",
                                            color: "#475569",
                                            border: "1px solid #e2e8f0",
                                            fontWeight: 500,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        } else {
                          // No filters at all - ALL accounts, no services, no regions
                          return (
                            <Box
                              sx={{
                                p: 2.5,
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                backgroundColor: "#f8fafc",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#64748b", mb: 1, textTransform: "uppercase" }}>
                                Scope Filters
                              </Typography>
                              <Typography sx={{ fontSize: "0.875rem", color: "#64748b" }}>
                                No filters applied. This rule will apply to all accounts, services, and regions.
                              </Typography>
                            </Box>
                          );
                        }
                      })()}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  );
                };
                return <>{rules.map((rule, i) => renderRuleAccordion(rule, i, rules))}</>;
                })()}
              </Box>
            </AccordionDetails>
          </Accordion>
        ));
      })()}

      {/* Important Information */}
      <Card sx={{ mb: 3, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#fff7ed" }}>
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                backgroundColor: "#f59e0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              <Typography sx={{ fontSize: "0.875rem", color: "#ffffff", fontWeight: 600 }}>!</Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#1e293b", mb: 1 }}>
                What happens when you create these rules?
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5, color: "#64748b", fontSize: "0.875rem", lineHeight: 1.8 }}>
                <li>These billing rules will be immediately applied to the customer's billing configuration.</li>
                <li>The rules will affect how costs are calculated, transferred, or adjusted in future billing cycles.</li>
                <li>For recurring rules, they will be active from the start date you specified.</li>
                <li>You can view, edit, or delete these rules later from the Billing Rules page.</li>
                <li>Changes may take effect in the next billing cycle depending on the rule frequency.</li>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 4 }}>
        <Button
          onClick={() => history.goBack()}
          disabled={isCreatingRules}
          sx={{
            textTransform: "none",
            color: "#475569",
          }}
        >
          Back
        </Button>
        <Button
          onClick={handleCreateRules}
          disabled={
            isCreatingRules ||
            (reviewNeedsIneligibleMetadata && isFetchingIneligibleServiceCodes)
          }
          variant="contained"
          sx={{
            textTransform: "none",
            backgroundColor: "#3b82f6",
            "&:hover": {
              backgroundColor: "#2563eb",
            },
            "&:disabled": {
              backgroundColor: "#cbd5e1",
            },
          }}
        >
          {isCreatingRules ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#ffffff" }} />
              Creating...
            </Box>
          ) : (
            "Create billing rules"
          )}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackbar((prev) => ({ ...prev, open: false }));
          dispatch(clearBillingManagementMessages());
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => {
            setSnackbar((prev) => ({ ...prev, open: false }));
            dispatch(clearBillingManagementMessages());
          }}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewBillingRules;
